import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Chip,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Switch,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Refresh,
  Clear,
  Save,
  Cancel,
  FilterList
} from '@mui/icons-material';

const Filieres = () => {
  const [data, setData] = useState([]);
  const [annees, setAnnees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [filtreAnnee, setFiltreAnnee] = useState('all');
  
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    filiere_id: '',
    code_filiere: '',
    intitule: '',
    libelle: '',
    diplome: '',
    nbAnneesDiplome: 0,
    anneeAccreditation: new Date().getFullYear(),
    etablissement: '',
    actif: true,
    annee_id: '1'
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  useEffect(() => {
    loadData();
    loadAnnees();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      
      const res = await axios.get('/api/filieres', { headers });
      console.log('📊 Filières chargées:', res.data?.length || 0);
      
      setData(res.data || []);
      setError('');
    } catch (err) {
      console.error('❌ Erreur chargement:', err);
      setError('Impossible de charger les filières');
    } finally {
      setLoading(false);
    }
  };

  const loadAnnees = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get('/api/annees', { headers });
      setAnnees(res.data || []);
      console.log('📊 Années chargées:', res.data?.length || 0);
    } catch (err) {
      console.error('❌ Erreur chargement années:', err);
    }
  };

  const generateFiliereId = () => {
    const maxId = data.reduce((max, item) => {
      const id = parseInt(item.filiere_id) || 0;
      return id > max ? id : max;
    }, 0);
    return String(maxId + 1);
  };

  const getDiplomeLabel = (diplome) => {
    if (!diplome) return '-';
    if (diplome.includes('Bac + 3')) return 'Licence';
    if (diplome.includes('Bac + 5') || diplome.includes('5 ans')) return 'Master';
    if (diplome.includes('2 ans')) return 'Master';
    if (diplome.includes('Préparatoires')) return 'CPI';
    return diplome;
  };

  const getAnneeLibelle = (anneeId) => {
    const annee = annees.find(a => a.annee_id === anneeId);
    return annee?.intitule || anneeId || 'Non définie';
  };

  const filteredData = data.filter(item => {
    const search = searchTerm.toLowerCase();
    const matchSearch = (item.intitule || '').toLowerCase().includes(search) ||
                        (item.code_filiere || '').toLowerCase().includes(search) ||
                        (item.libelle || '').toLowerCase().includes(search) ||
                        (item.filiere_id || '').toLowerCase().includes(search);
    
    if (!matchSearch) return false;
    
    if (filtreAnnee === 'all') return true;
    if (filtreAnnee === 'sans_annee') {
      return !item.annee_id || item.annee_id === '';
    }
    return item.annee_id === filtreAnnee;
  });

  const handleOpenModal = (mode, item = null) => {
    setModalMode(mode);
    setError('');
    setFormErrors({});
    setSubmitting(false);
    
    if (item) {
      console.log('📝 Édition de la filière:', item);
      
      setSelectedItem(item);
      setFormData({
        filiere_id: item.filiere_id || '',
        code_filiere: item.code_filiere || '',
        intitule: item.intitule || '',
        libelle: item.libelle || '',
        diplome: item.diplome || '',
        nbAnneesDiplome: item.nbAnneesDiplome || 0,
        anneeAccreditation: item.anneeAccreditation || new Date().getFullYear(),
        etablissement: item.etablissement || 'Ecole Supérieure des Multimédia, Informatique et Réseaux Casablanca',
        actif: item.actif !== undefined ? item.actif : true,
        annee_id: item.annee_id || '1'
      });
    } else {
      const newId = generateFiliereId();
      setFormData({
        filiere_id: newId,
        code_filiere: '',
        intitule: '',
        libelle: '',
        diplome: '',
        nbAnneesDiplome: 0,
        anneeAccreditation: new Date().getFullYear(),
        etablissement: 'Ecole Supérieure des Multimédia, Informatique et Réseaux Casablanca',
        actif: true,
        annee_id: '1'
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
    setFormErrors({});
    setSubmitting(false);
  };

  const handleSubmit = async () => {
    // Validation
    const errors = {};
    if (!formData.intitule) errors.intitule = 'L\'intitulé est obligatoire';
    if (!formData.libelle) errors.libelle = 'Le libellé est obligatoire';
    if (!formData.code_filiere) {
      // Générer automatiquement le code si vide
      setFormData(prev => ({
        ...prev,
        code_filiere: prev.libelle?.toUpperCase().replace(/\s/g, '-').replace(/[^A-Z0-9-]/g, '') || `FIL_${Date.now()}`
      }));
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setSubmitting(true);
      setLoading(true);
      const headers = getAuthHeaders();
      
      // ⭐ Construction explicite de l'objet pour la mise à jour
      const dataToSend = {
        filiere_id: formData.filiere_id || generateFiliereId(),
        code_filiere: formData.code_filiere || formData.libelle.toUpperCase().replace(/\s/g, '-').replace(/[^A-Z0-9-]/g, ''),
        intitule: formData.intitule || '',
        libelle: formData.libelle || '',
        diplome: formData.diplome || '',
        nbAnneesDiplome: parseInt(formData.nbAnneesDiplome) || 0,
        anneeAccreditation: parseInt(formData.anneeAccreditation) || new Date().getFullYear(),
        etablissement: formData.etablissement || 'Ecole Supérieure des Multimédia, Informatique et Réseaux Casablanca',
        actif: formData.actif !== undefined ? formData.actif : true,
        annee_id: formData.annee_id || '1'
      };
      
      console.log('📤 Données à envoyer:', dataToSend);
      
      let response;
      if (modalMode === 'add') {
        console.log('➕ Ajout d\'une nouvelle filière');
        response = await axios.post('/api/filieres', dataToSend, { headers });
        console.log('✅ Réponse ajout:', response.data);
        setSuccessMessage('Filière ajoutée avec succès');
      } else {
        // ⭐ MISE À JOUR : Utiliser le bon identifiant
        const idToUpdate = selectedItem?.filiere_id || selectedItem?._id || formData.filiere_id;
        console.log('📝 Mise à jour de la filière ID:', idToUpdate);
        console.log('📝 Données de mise à jour:', dataToSend);
        
        response = await axios.put(`/api/filieres/${idToUpdate}`, dataToSend, { headers });
        console.log('✅ Réponse mise à jour:', response.data);
        setSuccessMessage('Filière modifiée avec succès');
      }
      
      await loadData();
      handleCloseModal();
    } catch (err) {
      console.error('❌ Erreur complète:', err);
      console.error('❌ Réponse erreur:', err.response?.data);
      console.error('❌ Status:', err.response?.status);
      
      // Message d'erreur plus explicite
      let errorMessage = 'Erreur lors de l\'opération';
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Erreur spécifique pour les doublons
      if (errorMessage.includes('duplicate') || errorMessage.includes('unique')) {
        errorMessage = 'Ce code de filière existe déjà. Veuillez en utiliser un autre.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer définitivement ${item.intitule} ?`)) return;
    try {
      await axios.delete(`/api/filieres/${item.filiere_id}`, { headers: getAuthHeaders() });
      setSuccessMessage('Filière supprimée avec succès');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Mise à jour auto du code filière à partir du libellé en mode ajout
      if (field === 'libelle' && modalMode === 'add' && value) {
        const cleanCode = value.toUpperCase().replace(/\s/g, '-').replace(/[^A-Z0-9-]/g, '');
        if (cleanCode) {
          newData.code_filiere = cleanCode;
        }
      }
      
      return newData;
    });
    
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFiltreAnneeChange = (event, newValue) => {
    if (newValue !== null) {
      setFiltreAnnee(newValue);
      setPage(0);
    }
  };

  if (loading && data.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const countByAnnee = {};
  data.forEach(item => {
    const anneeId = item.annee_id || 'sans_annee';
    countByAnnee[anneeId] = (countByAnnee[anneeId] || 0) + 1;
  });

  return (
    <Box sx={{ p: 3 }}>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        message={error}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#1f2937' }}>
            🎓 Gestion des Filières
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredData.length} filière(s) trouvée(s)
            {filtreAnnee !== 'all' && ` (année ${getAnneeLibelle(filtreAnnee)})`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadData}>
            Actualiser
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal('add')}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            Nouvelle Filière
          </Button>
        </Stack>
      </Box>

      {/* Filtre par année */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList color="action" />
            <Typography variant="body2" fontWeight="500" color="text.secondary">
              Filtrer par année :
            </Typography>
          </Box>
          <ToggleButtonGroup
            value={filtreAnnee}
            exclusive
            onChange={handleFiltreAnneeChange}
            aria-label="filtre année"
            size="small"
          >
            <ToggleButton value="all" aria-label="toutes les années">
              Toutes
              <Chip 
                label={data.length} 
                size="small" 
                sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} 
              />
            </ToggleButton>
            {annees.map((annee) => (
              <ToggleButton key={annee.annee_id} value={annee.annee_id} aria-label={annee.intitule}>
                {annee.intitule}
                <Chip 
                  label={countByAnnee[annee.annee_id] || 0} 
                  size="small" 
                  sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} 
                />
              </ToggleButton>
            ))}
            <ToggleButton value="sans_annee" aria-label="sans année">
              Sans année
              <Chip 
                label={countByAnnee['sans_annee'] || 0} 
                size="small" 
                sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} 
              />
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField
          fullWidth
          placeholder="Rechercher par intitulé, code ou libellé..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}><Clear fontSize="small" /></IconButton>
              </InputAdornment>
            )
          }}
        />
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ background: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Libellé</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Diplôme</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Durée</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Niveaux</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Année</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => {
              const niveauCount = item.niveaux?.length || 0;
              const anneeLibelle = getAnneeLibelle(item.annee_id);
              return (
                <TableRow key={item._id || item.filiere_id} hover>
                  <TableCell>
                    <Chip 
                      label={item.code_filiere || '-'} 
                      size="small" 
                      sx={{ bgcolor: '#e0e7ff', color: '#4338ca', fontFamily: 'monospace' }} 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip label={item.libelle || '-'} size="small" sx={{ bgcolor: '#e0e7ff', color: '#4338ca' }} />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getDiplomeLabel(item.diplome)} 
                      size="small" 
                      sx={{ bgcolor: '#fef3c7', color: '#92400e' }} 
                    />
                  </TableCell>
                  <TableCell>{item.nbAnneesDiplome || '-'} ans</TableCell>
                  <TableCell>
                    <Chip 
                      label={`${niveauCount} niveau(x)`} 
                      size="small" 
                      sx={{ bgcolor: '#f3e8ff', color: '#7c3aed' }} 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={anneeLibelle} 
                      size="small" 
                      sx={{ bgcolor: '#dbeafe', color: '#1e40af' }} 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.actif ? '✅ Actif' : '❌ Inactif'}
                      size="small"
                      sx={{ bgcolor: item.actif ? '#dcfce7' : '#fee2e2', color: item.actif ? '#166534' : '#991b1b' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="Modifier">
                        <IconButton size="small" color="warning" onClick={() => handleOpenModal('edit', item)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredData.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">Aucune filière trouvée</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredData.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        labelRowsPerPage="Lignes par page"
        sx={{ mt: 2 }}
      />

      {/* ========== MODAL AJOUT / MODIFICATION ========== */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {modalMode === 'add' ? '➕ Ajouter une filière' : '✏️ Modifier la filière'}
            {modalMode === 'add' && (
              <Chip label="Nouveau" size="small" color="primary" sx={{ ml: 1 }} />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {/* CHAMP LIBELLÉ */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Libellé * (Nom court)"
                  name="libelle"
                  value={formData.libelle || ''}
                  onChange={(e) => handleFormChange('libelle', e.target.value)}
                  margin="normal"
                  required
                  placeholder="ex: M2 DCMD"
                  helperText="Nom court ou abréviation de la filière"
                  error={!!formErrors.libelle}
                />
                {formErrors.libelle && (
                  <Typography variant="caption" color="error">{formErrors.libelle}</Typography>
                )}
              </Grid>

              {/* CODE FILIÈRE */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Code filière"
                  name="code_filiere"
                  value={formData.code_filiere || ''}
                  onChange={(e) => handleFormChange('code_filiere', e.target.value)}
                  margin="normal"
                  placeholder="ex: 342287E_4706515"
                  helperText="Code unique de la filière"
                  error={!!formErrors.code_filiere}
                />
                {formErrors.code_filiere && (
                  <Typography variant="caption" color="error">{formErrors.code_filiere}</Typography>
                )}
              </Grid>

              {/* ANNÉE UNIVERSITAIRE */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Année Universitaire</InputLabel>
                  <Select
                    value={formData.annee_id || '1'}
                    onChange={(e) => handleFormChange('annee_id', e.target.value)}
                    label="Année Universitaire"
                  >
                    {annees.map((annee) => (
                      <MenuItem key={annee.annee_id} value={annee.annee_id}>
                        {annee.intitule}
                        {annee.estActive && (
                          <Chip label="Active" size="small" color="success" sx={{ ml: 1 }} />
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* ACTIF */}
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.actif !== undefined ? formData.actif : true}
                      onChange={(e) => handleFormChange('actif', e.target.checked)}
                    />
                  }
                  label={formData.actif ? '✅ Actif' : '❌ Inactif'}
                  sx={{ mt: 3 }}
                />
              </Grid>

              {/* INTITULÉ */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Intitulé *"
                  name="intitule"
                  value={formData.intitule || ''}
                  onChange={(e) => handleFormChange('intitule', e.target.value)}
                  margin="normal"
                  required
                  placeholder="ex: Master 2 : Développement Commercial et Marketing Digital"
                  error={!!formErrors.intitule}
                />
                {formErrors.intitule && (
                  <Typography variant="caption" color="error">{formErrors.intitule}</Typography>
                )}
              </Grid>

              {/* DIPLÔME */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Diplôme"
                  name="diplome"
                  value={formData.diplome || ''}
                  onChange={(e) => handleFormChange('diplome', e.target.value)}
                  margin="normal"
                  placeholder="ex: 2 ans après un diplôme 'B'"
                />
              </Grid>

              {/* DURÉE */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Durée (années)"
                  type="number"
                  name="nbAnneesDiplome"
                  value={formData.nbAnneesDiplome || 0}
                  onChange={(e) => handleFormChange('nbAnneesDiplome', parseInt(e.target.value) || 0)}
                  margin="normal"
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>

              {/* ANNÉE D'ACCRÉDITATION */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Année d'accréditation"
                  type="number"
                  name="anneeAccreditation"
                  value={formData.anneeAccreditation || new Date().getFullYear()}
                  onChange={(e) => handleFormChange('anneeAccreditation', parseInt(e.target.value) || new Date().getFullYear())}
                  margin="normal"
                />
              </Grid>

              {/* ÉTABLISSEMENT */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Établissement"
                  name="etablissement"
                  value={formData.etablissement || ''}
                  onChange={(e) => handleFormChange('etablissement', e.target.value)}
                  margin="normal"
                  placeholder="ex: Ecole Supérieure des Multimédia, Informatique et Réseaux Casablanca"
                />
              </Grid>

              {/* ID caché */}
              <Grid item xs={12} style={{ display: 'none' }}>
                <TextField
                  fullWidth
                  label="ID Filière"
                  name="filiere_id"
                  value={formData.filiere_id || ''}
                  onChange={(e) => handleFormChange('filiere_id', e.target.value)}
                  margin="normal"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} startIcon={<Cancel />} disabled={submitting}>
            Annuler
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            startIcon={submitting ? <CircularProgress size={20} /> : <Save />} 
            sx={{ bgcolor: '#667eea' }}
            disabled={!formData.intitule || !formData.libelle || submitting}
          >
            {submitting ? 'Enregistrement...' : (modalMode === 'add' ? 'Ajouter' : 'Modifier')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Filieres;


